import { useCallback, useState } from 'react';
import {
  applyPcfVisualCheckGrpc,
  clearPcfVisualCheckGrpc,
  type PcfVisualCheckResult,
} from '../../../shared/api/grpc/pcfVisualCheckClient';
import type { GroupBy } from '../../../shared/types/pcfExport.types';

interface UsePcfVisualCheckOptions {
  parameterName: string;
  parameterSource: 'Element' | 'Type';
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showError: (message: string) => void;
  announce: (message: string) => void;
}

export function usePcfVisualCheck(options: UsePcfVisualCheckOptions) {
  const [busy, setBusy] = useState(false);

  const reportError = useCallback((result: PcfVisualCheckResult) => {
    const message = result.versionMismatch
      ? 'Update the PipeEX Revit plugin to use Visual check.'
      : result.error || 'Visual check failed.';
    options.showError(message);
    options.announce(message);
  }, [options]);

  const apply = useCallback(async (mode: GroupBy) => {
    if (busy) {
      return;
    }
    setBusy(true);
    options.announce('Applying Visual check in the active Revit 3D view.');
    try {
      const result = await applyPcfVisualCheckGrpc({
        mode,
        parameterName: options.parameterName,
        parameterSource: options.parameterSource,
      });
      if (!result.success) {
        reportError(result);
        return;
      }

      const summary = `Visual check applied in ${result.viewName ?? 'the active 3D view'}: `
        + `${result.coloredElementCount ?? 0} elements, ${result.groupCount ?? 0} groups, `
        + `${result.filterCount ?? 0} filters.`;
      if ((result.ungroupedCount ?? 0) > 0) {
        const warning = `${summary} ${result.ungroupedCount} elements have no grouping value and are shown in red.`;
        options.showWarning(warning);
        options.announce(warning);
      } else {
        options.showSuccess(summary);
        options.announce(summary);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, options, reportError]);

  const reset = useCallback(async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    options.announce('Resetting Visual check in the active Revit 3D view.');
    try {
      const result = await clearPcfVisualCheckGrpc();
      if (!result.success) {
        reportError(result);
        return;
      }
      const message = `Visual check reset in ${result.viewName ?? 'the active 3D view'}: `
        + `${result.removedFilterCount ?? 0} PipeEX filters removed.`;
      options.showSuccess(message);
      options.announce(message);
    } finally {
      setBusy(false);
    }
  }, [busy, options, reportError]);

  return { busy, apply, reset };
}
